import React, { forwardRef } from 'react';
import { css, styled } from '@compiled/react';
import Badge from '@atlaskit/badge';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

import Tooltip from '@atlaskit/tooltip';

import { borderRadius, gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import messages from './messages.tsx';

export type Props = {
	attributeValueType:
		| 'object'
		| 'user'
		| 'group'
		| 'project'
		| 'opsgenieTeam'
		| 'bitbucket'
		| 'select';
	count: number;
	onClick: () => void;
};

export const MultiValueLozenge = forwardRef<HTMLElement, Props>(
	({ count, attributeValueType, ...restProps }, ref) => {
		const { formatMessage } = useIntl();
		const labelMessage = {
			object: {
				buttonLabel: formatMessage(messages.objectsLabel),
				tooltipLabel: formatMessage(messages.showAllObjects),
			},
			user: {
				buttonLabel: formatMessage(messages.usersLabel),
				tooltipLabel: formatMessage(messages.showAllUsers),
			},
			group: {
				buttonLabel: formatMessage(messages.groupsLabel),
				tooltipLabel: formatMessage(messages.showAllGroups),
			},
			project: {
				buttonLabel: formatMessage(messages.projectsLabel),
				tooltipLabel: formatMessage(messages.showAllProjects),
			},
			opsgenieTeam: {
				buttonLabel: formatMessage(messages.opsgenieTeamsLabel),
				tooltipLabel: formatMessage(messages.showAllOpsgenieTeams),
			},
			bitbucket: {
				buttonLabel: formatMessage(messages.bitbucketRepositoriesLabel),
				tooltipLabel: formatMessage(messages.showAllBbRepos),
			},
			select: {
				buttonLabel: formatMessage(messages.optionsLabel),
				tooltipLabel: formatMessage(messages.showAllOptions),
			},
		};
		const label = labelMessage[attributeValueType].buttonLabel;
		const tooltipContent = labelMessage[attributeValueType].tooltipLabel;

		return (
			<Tooltip content={tooltipContent} position="top">
				<LozengeButton
					innerRef={ref}
					{...restProps}
					testId="servicedesk-insight-shared-ui.ui.multi-value-lozenge.lozenge-button"
				>
					<LozengeLabel>{label}</LozengeLabel>
					<Count>
						<Badge max={false}>{count}</Badge>
					</Count>
					<ExpandIcon>
						<ChevronDownIcon label="" />
					</ExpandIcon>
				</LozengeButton>
			</Tooltip>
		);
	},
);

const lozengeStyles = css({
	boxShadow: `${token(
		'elevation.shadow.raised',
		'0 1px 1px rgba(9,30,66,0.25),0 0 1px 1px rgba(9,30,66,0.13)',
	)}`,
	display: 'inline-flex',
	alignItems: 'center',
	width: 'auto',
	boxSizing: 'border-box',
	height: '24px',
	margin: `0 0 ${token('space.050', '4px')}`,
	padding: `0 ${token('space.050', '4px')} ${token('space.025', '2px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: ` ${borderRadius}px`,
	cursor: 'pointer',

	/* Required to get truncation to work (total-width - horizontal-padding) */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxWidth: `calc(100% - ${gridSize}px)`,

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: `${token('elevation.surface.raised', colors.N0)}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: `${token('color.text', colors.N800)}`,

	'&:focus, &:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		backgroundColor: `${token('elevation.surface.raised', colors.N20)}`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		color: `${token('color.text', colors.N800)}`,
		textDecoration: 'none',
	},

	'&:active': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		backgroundColor: `${token('elevation.surface.raised', colors.N30)}`,
	},
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LozengeButton = styled.button<any>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...lozengeStyles,
	border: 'none',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Count = styled.div({
	flexShrink: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ExpandIcon = styled.div({
	/* Offset lozenge padding */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	marginRght: `-${gridSize / 2}px`,
	flexShrink: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LozengeLabel = styled.span({
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	margin: `0 ${token('space.050', '4px')}`,
});
